<script setup lang="ts">
import {computed, type HTMLAttributes} from "vue";
import {Check} from "@lucide/vue";
import {
    Stepper,
    StepperIndicator,
    StepperItem,
    StepperSeparator,
    StepperTitle,
    StepperTrigger
} from "@/core/components/ui/stepper";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import {cn} from "@/lib/utils";

/**
 * The steps of the course, and the way between them.
 *
 * The stepper says where the course stands — the step being worked on
 * active, the ones played checked — and opens a step in the thread when
 * clicked: a played one to reread its record, the current one to come back
 * to it. A step not reached yet has nothing to show and stays out of reach.
 */

const props = defineProps<{class?: HTMLAttributes["class"]}>();

const store = useMedicationCheckupStore();

/**
 * The steps to show, each paired with the label its definition declares.
 *
 * A step served without a declaration is skipped rather than shown
 * unnamed: the definition is the source of the course.
 */
const items = computed(() => {
    return store.steps.flatMap(step => {
        const declaration = store.declaredStep(step.definition_key);

        if (declaration === null) {
            return [];
        }

        return [{
            definitionKey: step.definition_key,
            label: declaration.label,
            status: step.status,
            openable: store.canOpenStep(step.definition_key),
            displayed: step.definition_key === store.displayedStep?.definition_key
        }];
    });
});

/**
 * The rank of the step being worked on, as the stepper counts them from one.
 *
 * A cycle with every step completed steps past the last one rather than
 * holding it active: the course is over, not in progress.
 */
const currentPosition = computed(() => {
    const key = store.currentStep?.definition_key;

    if (key === undefined) {
        return items.value.length + 1;
    }

    return items.value.findIndex(item => item.definitionKey === key) + 1;
});

/**
 * Open the step clicked, by the rank the stepper counts it at.
 *
 * The position shown stays the one of the course: opening a step moves the
 * thread, not where the course stands.
 */
const open = (position: number | undefined) => {
    const item = position === undefined ? undefined : items.value[position - 1];

    if (item !== undefined) {
        store.openStep(item.definitionKey);
    }
};
</script>

<template>
  <Stepper
    v-if="items.length"
    :model-value="currentPosition"
    :linear="false"
    :class="cn('flex w-full items-start gap-2', props.class)"
    @update:model-value="open"
  >
    <StepperItem
      v-for="(item, index) in items"
      :key="item.definitionKey"
      :step="index + 1"
      :completed="item.status === 'completed'"
      :disabled="!item.openable"
      class="relative flex w-full flex-col items-center justify-center"
    >
      <!--
        The whole column is the click target, but the feedback lands on the
        circle alone: a background on the column would paint over the line
        coming from the previous step, which crosses into this one.
      -->
      <StepperTrigger
        class="group/trigger w-full gap-0 p-0 rounded-md enabled:cursor-pointer focus-visible:outline-none"
      >
        <StepperIndicator
          class="size-[30px] shrink-0 border-2 border-gray text-sm font-semibold text-muted-foreground
            transition-transform duration-150 group-hover/trigger:scale-110
            group-focus-visible/trigger:ring-2 group-focus-visible/trigger:ring-offset-2
            group-focus-visible/trigger:ring-offset-white group-focus-visible/trigger:ring-primary-lightest
            group-data-[disabled]:opacity-100 group-data-[disabled]:text-muted-foreground
            group-data-[state=active]:border-primary group-data-[state=active]:bg-primary group-data-[state=active]:text-white
            group-data-[state=completed]:border-secondary group-data-[state=completed]:bg-secondary group-data-[state=completed]:text-primary-dark"
          :class="item.displayed && 'ring-2 ring-offset-2 ring-offset-white ring-primary'"
        >
          <Check v-if="item.status === 'completed'" class="size-4" :stroke-width="3" />
          <span v-else>{{ index + 1 }}</span>
        </StepperIndicator>

        <StepperTitle
          class="mt-1.5 text-xs font-normal leading-tight whitespace-normal text-muted-foreground text-center
            transition-colors group-hover/trigger:text-primary
            group-data-[state=active]:font-medium group-data-[state=active]:text-primary
            group-data-[state=completed]:text-gray-dark"
          :class="item.displayed && 'font-medium text-primary'"
        >
          {{ item.label }}
        </StepperTitle>
      </StepperTrigger>

      <StepperSeparator
        v-if="index !== items.length - 1"
        class="absolute left-[calc(50%+21px)] right-[calc(-50%+21px)] top-[14px] block h-0.5 shrink-0 rounded-full
          bg-gray group-data-[state=completed]:bg-secondary"
      />
    </StepperItem>
  </Stepper>
</template>
